import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Bell, Sun, Moon, LogOut, User, ChevronDown, Plus } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import type { Notification } from '@wrapforce/shared';
import { useThemeStore } from '@/stores/theme';
import { useMe } from '@/features/session/api';
import { useMarkNotificationsRead, useNotifications } from '@/features/notifications/api';
import { useTrialStatus } from '@/features/trial/useTrialStatus';
import { useWorkspaceStore } from '@/stores/workspace';
import { useSession } from '@/features/session/useSession';
import { signOut } from '@/features/auth/api';
import { useLocaleFormatters } from '@/lib/format';
import { LocationSwitcher } from '@/features/locations/LocationSwitcher';
import { cn } from '@/lib/cn';
import { LanguageSwitcher } from './LanguageSwitcher';
import { GlobalSearch } from './GlobalSearch';
import { HelpMenu } from '@/features/tour/HelpMenu';

export function Topbar({ title }: { title: string }) {
  const { t } = useTranslation();
  const { pref, setPref } = useThemeStore();
  const { data: me } = useMe();
  const { session } = useSession();
  const { activeWorkspaceId, setActiveWorkspaceId } = useWorkspaceStore();
  const { isTrial, daysLeft, expired } = useTrialStatus(activeWorkspaceId);
  const { formatDate } = useLocaleFormatters();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // The quick-add button follows the current section: customers and leads open
  // their list page's create dialog (via ?new=1); everything else, including
  // jobs (which are only created from quotes), starts a new quote.
  const quickAdd = pathname.startsWith('/customers')
    ? { to: '/customers?new=1', labelKey: 'topbar.newCustomer' }
    : pathname.startsWith('/leads')
      ? { to: '/leads?new=1', labelKey: 'topbar.newLead' }
      : { to: '/quotes/new', labelKey: 'topbar.newQuote' };

  return (
    <header className="flex flex-none items-center gap-2.5 border-b border-border bg-surface px-4 py-3.5 md:gap-3.5 md:px-6">
      <h1 className="min-w-0 truncate font-display text-base font-bold uppercase tracking-[.05em] text-ink md:text-lg">{title}</h1>
      <span className="hidden whitespace-nowrap border-l border-border pl-3.5 font-mono text-[11px] text-muted xl:inline">
        {formatDate(new Date(), 'EEE MMM dd · HH:mm').toUpperCase()}
      </span>

      {isTrial && !expired && daysLeft !== null ? (
        <span
          className={cn(
            'whitespace-nowrap px-3 py-1 text-xs font-bold',
            daysLeft <= 3 ? 'bg-warning/15 text-warning' : 'bg-surface-2 text-muted',
          )}
        >
          {t('topbar.trialDaysLeft', { count: daysLeft })}
        </span>
      ) : null}

      <div className="ml-auto flex flex-none items-center gap-2 md:gap-2.5">
        <div data-tour="topbar-search">
          <GlobalSearch workspaceId={activeWorkspaceId} />
        </div>
        <Link
          to={quickAdd.to}
          aria-label={t(quickAdd.labelKey)}
          data-tour="topbar-quick-add"
          onClick={(e) => {
            // Navigate after the click event finishes: the ?new=1 targets mount
            // a Radix dialog that would otherwise treat this same click as an
            // outside interaction and instantly dismiss itself.
            e.preventDefault();
            setTimeout(() => navigate(quickAdd.to), 0);
          }}
          className="flex min-h-11 items-center gap-1.5 whitespace-nowrap bg-accent px-3.5 py-2 text-[13px] font-bold tracking-[.02em] text-on-accent hover:bg-accent-hover md:min-h-0 md:px-4"
        >
          <Plus size={14} strokeWidth={2.5} />
          <span className="hidden sm:inline">{t(quickAdd.labelKey)}</span>
        </Link>
        <div className="hidden md:block">
          <WorkspaceSwitcher
            memberships={me?.memberships ?? []}
            activeId={activeWorkspaceId}
            onSwitch={(id) => session?.user.id && setActiveWorkspaceId(session.user.id, id)}
          />
        </div>
        <LocationSwitcher />
        <NotificationBell />
        <div className="hidden md:block">
          <LanguageSwitcher />
        </div>
        <div className="hidden md:block">
          <HelpMenu />
        </div>
        <button
          type="button"
          onClick={() => setPref(pref === 'dark' ? 'light' : 'dark')}
          className="hidden h-8 w-8 place-items-center border border-border text-muted hover:bg-surface-2 md:grid"
          aria-label={t('topbar.toggleTheme')}
        >
          {pref === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>
        <div className="hidden md:block">
          <AvatarMenu
            name={me?.fullName || me?.email || 'Account'}
            onSignOut={async () => {
              await signOut();
              navigate('/login', { replace: true });
            }}
          />
        </div>
      </div>
    </header>
  );
}

function WorkspaceSwitcher({
  memberships,
  activeId,
  onSwitch,
}: {
  memberships: { workspaceId: string; name: string }[];
  activeId: string | null;
  onSwitch: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const active = memberships.find((m) => m.workspaceId === activeId);
  if (memberships.length <= 1) return null;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 border border-border px-3 py-1.5 text-[13px] font-medium text-ink hover:bg-surface-2"
        >
          {active?.name ?? t('topbar.selectWorkspace')}
          <ChevronDown size={14} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="z-50 w-56 border border-border bg-surface p-1.5 shadow-md"
        >
          {memberships.map((m) => (
            <button
              key={m.workspaceId}
              type="button"
              onClick={() => {
                onSwitch(m.workspaceId);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center px-2.5 py-2 text-left text-sm hover:bg-surface-2',
                m.workspaceId === activeId && 'font-semibold text-accent-ink',
              )}
            >
              {m.name}
            </button>
          ))}
          <a
            href="/onboarding"
            className="mt-1 flex items-center gap-1.5 border-t border-border px-2.5 pt-2 text-sm font-medium text-accent-ink"
          >
            <Plus size={13} /> {t('topbar.createWorkspace')}
          </a>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function notificationHref(n: Notification): string | null {
  const entity = n.entity as { quoteId?: string; jobId?: string; leadId?: string };
  if (entity.jobId) return `/jobs/${entity.jobId}`;
  if (entity.quoteId) return `/quotes/${entity.quoteId}`;
  if (entity.leadId) return `/leads?leadId=${entity.leadId}`;
  return null;
}

function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data } = useNotifications();
  const markRead = useMarkNotificationsRead();
  const { formatRelative } = useLocaleFormatters();
  const items = data?.items ?? [];
  const unread = items.filter((n) => !n.readAt);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          data-tour="topbar-notifications"
          className="relative grid h-11 w-11 place-items-center border border-border text-muted hover:bg-surface-2 md:h-8 md:w-8"
          aria-label={t('topbar.notifications')}
        >
          <Bell size={16} />
          {unread.length > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center bg-accent px-1 text-[10px] font-bold text-on-accent">
              {unread.length > 9 ? '9+' : unread.length}
            </span>
          ) : null}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="z-50 flex w-80 flex-col border border-border bg-surface p-1.5 shadow-md">
          <div className="flex items-center justify-between px-2 py-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-muted">{t('topbar.notifications')}</span>
            {unread.length > 0 ? (
              <button type="button" onClick={() => markRead.mutate({ all: true })} className="text-xs font-semibold text-accent-ink">
                {t('topbar.markAllRead')}
              </button>
            ) : null}
          </div>
          <div className="flex max-h-96 flex-col overflow-y-auto">
            {items.length === 0 ? <p className="px-2.5 py-4 text-center text-sm text-muted">{t('topbar.allCaughtUp')}</p> : null}
            {items.map((n) => {
              const href = notificationHref(n);
              const body = (
                <div className={cn('flex flex-col gap-0.5 px-2.5 py-2 text-left hover:bg-surface-2', !n.readAt && 'bg-accent/5')}>
                  <div className="flex items-center gap-1.5">
                    {!n.readAt ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" /> : null}
                    <span className="text-sm font-semibold text-ink">{n.title}</span>
                  </div>
                  {n.body ? <span className="text-xs text-muted">{n.body}</span> : null}
                  <span className="text-[11px] text-muted">{formatRelative(new Date(n.createdAt))}</span>
                </div>
              );
              return href ? (
                <Link
                  key={n.id}
                  to={href}
                  onClick={() => {
                    if (!n.readAt) markRead.mutate({ ids: [n.id] });
                    setOpen(false);
                  }}
                >
                  {body}
                </Link>
              ) : (
                <button key={n.id} type="button" className="w-full" onClick={() => !n.readAt && markRead.mutate({ ids: [n.id] })}>
                  {body}
                </button>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function AvatarMenu({ name, onSignOut }: { name: string; onSignOut: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="grid h-8 w-8 place-items-center bg-ink text-xs font-semibold text-surface">
          {name.slice(0, 2).toUpperCase()}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="z-50 w-48 border border-border bg-surface p-1.5 shadow-md">
          <a href="/settings" className="flex items-center gap-2 px-2.5 py-2 text-sm hover:bg-surface-2">
            <User size={14} /> {t('topbar.profile')}
          </a>
          <button
            type="button"
            onClick={onSignOut}
            className="flex w-full items-center gap-2 px-2.5 py-2 text-left text-sm text-danger hover:bg-surface-2"
          >
            <LogOut size={14} /> {t('topbar.signOut')}
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
